import { FC, ReactNode } from 'react';
import { IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useBreakpoints } from '../theme/useBreakpoints';

const useStyles = createUseStyles({
    modal: {
        '--height': 'auto'
    }
});

export interface IBottomSheetModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    // Defaults to Ionic's behavior (dismiss on backdrop tap). Set false when the sheet hosts
    // nested overlays (popovers/alerts) whose dismissal would otherwise cascade and close it.
    backdropDismiss?: boolean;
    'data-id'?: string;
    children: ReactNode;
}

/**
 * A modal that presents as a bottom sheet on narrow screens and a normal centered
 * modal on desktop. Below `md` the modal is full-width, so a bottom sheet reads
 * correctly; at/above `md` Ionic centers the modal as a card, where the sheet
 * breakpoints would leave it awkwardly anchored to the bottom of the viewport.
 * Dropping the breakpoint props lets Ionic render its default centered modal.
 */
export const BottomSheetModal: FC<IBottomSheetModalProps> = ({ isOpen, onDidDismiss, backdropDismiss, children, ...rest }) => {
    const classes = useStyles();
    const { isBelowBreakpoint } = useBreakpoints();
    const isSheet = isBelowBreakpoint('md');

    const sheetProps = isSheet ? { initialBreakpoint: 1, breakpoints: [0, 1] } : {};

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onDidDismiss}
            backdropDismiss={backdropDismiss}
            className={classes.modal}
            {...sheetProps}
            {...rest}
        >
            {children}
        </IonModal>
    );
};
